import React, { useEffect, useState } from "react";
import { Alert, Box, Button, TextField, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { CloudUpload } from "@mui/icons-material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import axios from "axios";
import { API_ENDPOINT } from "../../constants/constants";
import { getHeader } from "../../utils/util";
import useApiEp from "../hooks/useApiEp";
import ToolsLayout, { ToolsState } from "../tools/ToolsLayout";
import { invalidateTools } from "../tools/toolsData";

export default function BannerForm() {
  const { id } = useParams(),
    navigate = useNavigate(),
    client = useQueryClient();
  const query = useApiEp(`admin/banners/${encodeURIComponent(id || "")}`, Boolean(id));
  const [title, setTitle] = useState(""),
    [file, setFile] = useState(null),
    [preview, setPreview] = useState(""),
    [saving, setSaving] = useState(false),
    [error, setError] = useState("");
  useEffect(() => {
    setTitle(query.data?.title || "");
  }, [query.data]);
  useEffect(() => {
    if (!file) {
      setPreview("");
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const chooseImage = (event) => {
    const next = event.target.files[0];
    event.target.value = "";
    if (!next) return;
    if (
      !["image/jpeg", "image/png", "image/webp"].includes(next.type) ||
      next.size > 5 * 1024 * 1024
    ) {
      setError("Choose a JPG, PNG or WebP image up to 5 MB.");
      return;
    }
    setFile(next);
    setError("");
  };
  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    if (!title.trim()) {
      setError("Enter a banner title.");
      return;
    }
    if (!id && !file) {
      setError("Upload an image for this banner.");
      return;
    }
    if (id && (!query.data || query.isError)) return;
    setSaving(true);
    setError("");
    try {
      const body = new FormData();
      body.append("title", title.trim());
      if (file) body.append("image", file);
      const headers = { ...getHeader().headers };
      Object.keys(headers).forEach((key) => {
        if (key.toLowerCase() === "content-type") delete headers[key];
      });
      await axios({
        method: id ? "put" : "post",
        url: `${API_ENDPOINT}admin/banners${id ? "/" + encodeURIComponent(id) : ""}`,
        data: body,
        headers,
      });
      await invalidateTools(client);
      setSaving(false);
      navigate("/home/banners");
    } catch (err) {
      setError(err.response?.data?.message || "Unable to save the banner. Please try again.");
      setSaving(false);
    }
  };
  const displayImage = preview || query.data?.imageUrl;
  return (
    <ToolsLayout
      title={id ? "Edit banner" : "Add banner"}
      description="Preview the full image and title before saving it to the market."
    >
      <section className="tools-panel" style={{ maxWidth: 900, margin: "auto" }}>
        <ToolsState
          loading={Boolean(id) && query.isLoading}
          error={Boolean(id) && query.isError}
          retry={query.refetch}
          empty={Boolean(id) && !query.isLoading && !query.isError && !query.data}
          emptyTitle="Banner not found"
        >
          <Box component="form" onSubmit={submit} sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
              Banner details
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              fullWidth
              required
              label="Banner title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              disabled={saving}
              helperText="Use a short, descriptive title to identify this promotion."
            />
            <Box sx={{ my: 3 }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUpload />}
                disabled={saving}
              >
                {file ? "Change image" : id ? "Replace image" : "Upload image"}
                <input
                  hidden
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={chooseImage}
                />
              </Button>
              {file && (
                <Button disabled={saving} onClick={() => setFile(null)}>
                  Discard new image
                </Button>
              )}
              <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                JPG, PNG or WebP · up to 5 MB · a wide image works well for market banners.
              </Typography>
            </Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Image preview
            </Typography>
            {displayImage ? (
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.default",
                  borderRadius: 2,
                  p: 1,
                }}
              >
                <img
                  src={displayImage}
                  alt={title || "Banner preview"}
                  style={{ width: "100%", maxHeight: 350, objectFit: "contain", display: "block" }}
                />
              </Box>
            ) : (
              <Box
                sx={{ p: 6, textAlign: "center", bgcolor: "background.default", borderRadius: 2 }}
              >
                <Typography color="text.secondary">Upload an image to preview it here.</Typography>
              </Box>
            )}
            <div className="tools-actions" style={{ marginTop: 24, justifyContent: "flex-end" }}>
              <Button disabled={saving} component={Link} to="/home/banners">
                Cancel
              </Button>
              <LoadingButton type="submit" variant="contained" loading={saving}>
                Save banner
              </LoadingButton>
            </div>
          </Box>
        </ToolsState>
      </section>
    </ToolsLayout>
  );
}
